import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

const ManualDepositsAdmin = () => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchDeposits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getManualDeposits(statusFilter);
      setDeposits(response.deposits || []);
    } catch (error) {
      console.error('Error fetching manual deposits:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  const processDeposit = async (depositId) => {
    try {
      await adminAPI.processManualDeposit(depositId);
      alert('Deposit processed successfully!');
      fetchDeposits();
    } catch (error) {
      console.error('Error processing deposit:', error);
      alert('Error processing deposit. Please try again.');
    }
  };

  const rejectDeposit = async (depositId) => {
    if (window.confirm('Are you sure you want to reject this deposit request?')) {
      try {
        await adminAPI.rejectManualDeposit(depositId);
        alert('Deposit request rejected.');
        fetchDeposits();
      } catch (error) {
        console.error('Error rejecting deposit:', error);
        alert('Error rejecting deposit. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="p-4">Loading manual deposits...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#1a1a1a' }}>Manual Deposits</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              background: 'white',
              fontSize: '14px'
            }}
          >
            <option value="pending">Pending</option>
            <option value="processed">Processed</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={fetchDeposits}
            style={{
              padding: '8px 16px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.background = '#0056b3'}
            onMouseOut={(e) => e.target.style.background = '#007bff'}
          >
            Refresh
          </button>
        </div>
      </div>

      {deposits.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666' }}>
          No {statusFilter === 'all' ? '' : statusFilter} manual deposits found.
        </div>
      ) : (
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0',
          overflow: 'hidden'
        }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {deposits.map((deposit) => (
              <li key={deposit._id} style={{ 
                padding: '24px', 
                borderBottom: '1px solid #e0e0e0',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <p style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        margin: 0, 
                        color: '#1a1a1a' 
                      }}>
                        {deposit.user_name} ({deposit.user_email})
                      </p>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        margin: 0, 
                        color: '#28a745' 
                      }}>
                        ${deposit.amount.toFixed(2)}
                      </p>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      <p style={{ fontSize: '13px', color: '#666', margin: '4px 0' }}>
                        <span style={{ fontWeight: '500' }}>Method:</span> {deposit.payment_method.toUpperCase()}
                        {deposit.reference_number && (
                          <span> • <span style={{ fontWeight: '500' }}>Reference:</span> #{deposit.reference_number}</span>
                        )}
                        {deposit.reference_note && (
                          <span> • <span style={{ fontWeight: '500' }}>Note:</span> {deposit.reference_note}</span>
                        )}
                      </p>
                      <p style={{ fontSize: '13px', color: '#666', margin: '4px 0' }}>
                        <span style={{ fontWeight: '500' }}>Requested:</span> {formatDate(deposit.created_at)}
                      </p>
                      {deposit.processed_at && (
                        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0' }}>
                          <span style={{ fontWeight: '500' }}>Processed:</span> {formatDate(deposit.processed_at)}
                        </p>
                      )}
                      {deposit.admin_note && (
                        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0' }}>
                          <span style={{ fontWeight: '500' }}>Admin Note:</span> {deposit.admin_note}
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ marginLeft: '16px', flexShrink: 0 }}>
                    <span style={{
                      display: 'inline-flex',
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '20px',
                      textTransform: 'uppercase',
                      ...(deposit.status === 'pending' ? {
                        background: '#fff3cd',
                        color: '#856404'
                      } : deposit.status === 'processed' ? {
                        background: '#d4edda',
                        color: '#155724'
                      } : {
                        background: '#f8d7da',
                        color: '#721c24'
                      })
                    }}>
                      {deposit.status}
                    </span>
                  </div>
                </div>
                
                {deposit.status === 'pending' && (
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => processDeposit(deposit._id)}
                      style={{
                        padding: '6px 12px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#218838'}
                      onMouseOut={(e) => e.target.style.background = '#28a745'}
                    >
                      Process Deposit
                    </button>
                    <button
                      onClick={() => rejectDeposit(deposit._id)}
                      style={{
                        padding: '6px 12px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#c82333'}
                      onMouseOut={(e) => e.target.style.background = '#dc3545'}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ManualDepositsAdmin;